/**
 * BFS-based pathfinding for monster movement.
 * 4-connected grid; tower cells are impassable; start/end always passable.
 */

import type { GameSession } from "./session.js";
import type { Cell } from "./types.js";

const DIR = [[-1, 0], [1, 0], [0, -1], [0, 1]] as const;

/**
 * BFS from startCell to endCell on a grid of given size, avoiding blocked cells.
 * startCell and endCell are always treated as passable regardless of blockedCells.
 * Returns the full cell path (inclusive) or null if unreachable.
 */
function bfsSegment(
  rows: number,
  cols: number,
  startCell: Cell,
  endCell: Cell,
  blockedCells: Set<string>,
): Cell[] | null {
  const key = (r: number, c: number) => `${r},${c}`;

  if (startCell.row === endCell.row && startCell.col === endCell.col) {
    return [{ row: startCell.row, col: startCell.col }];
  }

  const startKey = key(startCell.row, startCell.col);
  const endKey = key(endCell.row, endCell.col);

  const prev = new Map<string, string | null>();
  prev.set(startKey, null);
  const queue: string[] = [startKey];
  let head = 0;

  while (head < queue.length) {
    const cur = queue[head++];
    if (cur === endKey) break;
    const [cr, cc] = cur.split(",").map(Number);
    for (const [dr, dc] of DIR) {
      const nr = cr + dr;
      const nc = cc + dc;
      if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
      const nk = key(nr, nc);
      if (prev.has(nk)) continue;
      if (blockedCells.has(nk) && nk !== startKey && nk !== endKey) continue;
      prev.set(nk, cur);
      queue.push(nk);
    }
  }

  if (!prev.has(endKey)) return null;

  const path: Cell[] = [];
  let cur: string | null = endKey;
  while (cur !== null) {
    const [r, c] = cur.split(",").map(Number);
    path.push({ row: r, col: c });
    cur = prev.get(cur) ?? null;
  }
  path.reverse();
  return path;
}

/**
 * Full monster/road path: **shortest** 4-connected route from first waypoint (rift)
 * to last (altar). Intermediate waypoints in field config are ignored for routing so
 * monsters and the dashed line do not take forced detours.
 * Returns [] if unreachable.
 */
export function computeFullPath(
  rows: number,
  cols: number,
  waypoints: Cell[],
  blockedCells: Set<string>,
): Cell[] {
  if (waypoints.length < 2) return waypoints.length === 1 ? [{ ...waypoints[0] }] : [];
  const start = waypoints[0];
  const end = waypoints[waypoints.length - 1];
  const seg = bfsSegment(rows, cols, start, end, blockedCells);
  return seg ?? [];
}

/**
 * BFS from start cell until end cell reached or frontier empty.
 * isPassable(r, c) decides whether a cell is traversable.
 * Returns true if start can reach end.
 */
export function bfsReachable(
  rows: number,
  cols: number,
  start: Cell,
  end: Cell,
  isPassable: (r: number, c: number) => boolean,
): boolean {
  if (start.row === end.row && start.col === end.col) return true;
  const idx = (r: number, c: number) => r * cols + c;
  const visited = new Uint8Array(rows * cols);
  const queue: Cell[] = [start];
  visited[idx(start.row, start.col)] = 1;
  while (queue.length > 0) {
    const { row, col } = queue.shift()!;
    if (row === end.row && col === end.col) return true;
    for (const [dr, dc] of DIR) {
      const nr = row + dr;
      const nc = col + dc;
      if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
      const ni = idx(nr, nc);
      if (visited[ni]) continue;
      if (!isPassable(nr, nc)) continue;
      visited[ni] = 1;
      queue.push({ row: nr, col: nc });
    }
  }
  return false;
}

/**
 * BFS on a subgraph of allowed cells (start/end always passable).
 * allowedCells: "row,col" keys — only these cells are traversable.
 * blockedCells: additional blocked keys (towers) applied on top.
 * Returns path inclusive, or null if unreachable.
 */
export function bfsInAllowed(
  rows: number,
  cols: number,
  startCell: Cell,
  endCell: Cell,
  allowedCells: Set<string>,
  blockedCells: Set<string>,
): Cell[] | null {
  const key = (r: number, c: number) => `${r},${c}`;

  if (startCell.row === endCell.row && startCell.col === endCell.col) {
    return [{ row: startCell.row, col: startCell.col }];
  }

  const startKey = key(startCell.row, startCell.col);
  const endKey = key(endCell.row, endCell.col);

  const prev = new Map<string, string | null>();
  prev.set(startKey, null);
  const queue: string[] = [startKey];
  let head = 0;

  while (head < queue.length) {
    const cur = queue[head++];
    if (cur === endKey) break;
    const [cr, cc] = cur.split(",").map(Number);
    for (const [dr, dc] of DIR) {
      const nr = cr + dr;
      const nc = cc + dc;
      if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
      const nk = key(nr, nc);
      if (prev.has(nk)) continue;
      // Must be in allowedCells OR be start/end; must not be in blockedCells (unless start/end).
      const isStartOrEnd = nk === startKey || nk === endKey;
      if (!isStartOrEnd && !allowedCells.has(nk)) continue;
      if (!isStartOrEnd && blockedCells.has(nk)) continue;
      prev.set(nk, cur);
      queue.push(nk);
    }
  }

  if (!prev.has(endKey)) return null;

  const path: Cell[] = [];
  let cur: string | null = endKey;
  while (cur !== null) {
    const [r, c] = cur.split(",").map(Number);
    path.push({ row: r, col: c });
    cur = prev.get(cur) ?? null;
  }
  path.reverse();
  return path;
}

/**
 * BFS path from startCell to endCell restricted to walkableCells minus blocking tower cells.
 * Monsters may only travel the walkable path; traps placed on it remain passable.
 * start/end are always passable.
 */
export function findPath(
  session: GameSession,
  startCell: Cell,
  endCell: Cell
): Cell[] | null {
  const { rows, cols } = session.field;

  // Build allowed set from walkableCells.
  const allowed = new Set<string>();
  for (const c of session.field.walkableCells) {
    allowed.add(`${c.row},${c.col}`);
  }

  // Build blocked set from path-blocking buildings. Traps are walkable.
  const blocked = new Set<string>();
  for (const t of session.towers) {
    if (t.type === "Trap") continue;
    blocked.add(`${t.cell.row},${t.cell.col}`);
  }

  return bfsInAllowed(rows, cols, startCell, endCell, allowed, blocked);
}

/**
 * Returns the next cell the monster should step towards (path[1]),
 * or null if already at target or no path exists.
 */
export function nextCellTowardsTarget(
  session: GameSession,
  from: Cell,
  to: Cell
): Cell | null {
  const path = findPath(session, from, to);
  if (!path || path.length < 2) return null;
  return path[1];
}
